import { useSlideshowContext } from '@fukku/slideshow/hooks/useSlideshowContext/useSlideshowContext'
import { useSlideshowTouch } from '@fukku/slideshow/hooks/useSlideshowTouch/useSlideshowTouch'
import styles from '@fukku/slideshow/Slideshow.module.scss'
import { SlideshowAnimation } from '@fukku/slideshow/types/Slideshow'
import { addTestIdToChildren } from '@fukku/slideshow/utils/addTestIdToChildren'
import { type CSSProperties, useCallback, useMemo } from 'react'
import { cx } from 'utils/cx'

type SlideshowItemsProps = {
	readonly initialAnimation: SlideshowAnimation | undefined
}

export const SlideshowItems = ({ initialAnimation }: SlideshowItemsProps) => {
	const {
		slideshowRef,
		items,
		nItems,
		defaultActiveItemIndex,
		updateStyles,
		onAnimationEnd,
	} = useSlideshowContext()

	const { onTouchEnd, onTouchMove, onTouchStart } = useSlideshowTouch()

	const shouldAnimate = !!initialAnimation && nItems > 1

	const animationClasses = useMemo(() => {
		if (!shouldAnimate) {
			return []
		}

		const classes = [styles.animate]
		if (initialAnimation === SlideshowAnimation.Long) {
			classes.push(styles.longAnimation)
		}
		if (initialAnimation === SlideshowAnimation.Short) {
			classes.push(styles.shortAnimation)
		}
		return classes
	}, [shouldAnimate, initialAnimation])

	const onTransitionEnd = useCallback(() => {
		updateStyles({ transitionDuration: 0 })
	}, [updateStyles])

	return (
		<div
			onTransitionEnd={onTransitionEnd}
			onTouchStart={onTouchStart}
			onTouchMove={onTouchMove}
			onTouchEnd={onTouchEnd}
			onAnimationEnd={onAnimationEnd}
			className={cx(styles.slideshow, ...animationClasses)}
			ref={slideshowRef}
			data-testid='slideshow'
			style={
				{
					'--slideshow-offset': '0px',
					'--slideshow-active-item-index': `${defaultActiveItemIndex}`,
					'--slideshow-transition-duration': '0ms',
				} as CSSProperties
			}
		>
			{addTestIdToChildren(items)}
		</div>
	)
}
